import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ValidatorFn } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { environment } from '@shared/environments';
import {
  AddingByGroupComponent,
  CustomValidators,
  FormInputCheckboxComponent,
  FormInputRadioItemInterface,
  FormInputTextComponent,
  getValidationConfig,
} from '@shared/form';
import { ButtonComponent } from '@shared/ui/button';
import { InfoComponent, InfoTypeEnum } from '@shared/ui/info';
import { ModalCloseDirective } from '@shared/ui/modals';
import { isEmpty } from 'lodash-es';
import { NgScrollbar } from 'ngx-scrollbar';
import { finalize } from 'rxjs';
import {
  AddSiteModalDataInterface,
  KEEN_DNS_PATTERN,
  SERVICE_CODE_PATTERN,
} from './add-site-modal.interface';

@Component({
  selector: 'shared-store-import-nodes-modal',
  imports: [
    MatDialogModule,
    ButtonComponent,
    FormInputTextComponent,
    InfoComponent,
    AddingByGroupComponent,
    FormInputCheckboxComponent,
    ModalCloseDirective,
    NgScrollbar,
    MatTooltip,
  ],
  templateUrl: './add-site-modal.component.html',
  styleUrls: ['./add-site-modal.component.css'],
})
export class AddSiteModalComponent {
  dialogRef = inject<MatDialogRef<AddSiteModalComponent>>(MatDialogRef);
  private readonly formBuilder = inject(FormBuilder);
  data: AddSiteModalDataInterface = inject(MAT_DIALOG_DATA);

  protected addressTitle = $localize`:@@addSiteModalVendorDNSAddressTitle:${environment.vendor?.dnsName}:VENDOR_DNS_NAME: address *`;
  protected infoText = $localize`:@@addSiteModalInfoText:Enter the login and password of the controller that you want to add using ${environment.vendor?.dnsName}:VENDOR_DNS_NAME: or service tag`;
  protected siteAgreementText = $localize`:@@addSiteAgreement:By clicking "Add site" you agree to add the ${environment.vendor?.name}:VENDOR_NAME: device to the extended warranty programme`;

  @HostListener('window:keyup.Enter', ['$event'])
  onEnterClick(event: KeyboardEvent): void {
    this.addSite();
  }

  isSubmitting = signal(false);

  warningText = $localize`:@@addSiteWarningText:For using ${environment?.vendor?.name}:VENDOR_NAME: RMM, the ${environment?.vendor?.osName}:VENDOR_OS_NAME: of your controller should be 3.7.5 or later. Please view <a href="https://${environment?.vendor?.docsUrl}:VENDOR_DOCS_URL:/eu/titan/kn-1811/en/35334-site-addition.html" target="_blank">User's Manual</a> for more details`;

  addingBy: 'servicetag' | 'keendns' = 'servicetag';

  addingByGroupData: FormInputRadioItemInterface[] = [
    {
      id: 'servicetag',
      value: 'servicetag',
      title: $localize`:@@addSiteModalAddSiteByServiceTagTitle:Add site by Service tag`,
      name: 'addingBy',
      label: $localize`:@@addSiteModalAddSiteByServiceTagLabel:Service tag`,
      testId: 'add-site-modal-service-tag-radio-button',
    },
    {
      id: 'keendns',
      value: 'keendns',
      title: $localize`:@@addSiteModalAddSiteByVendorDNSTitle:Add site by ${environment?.vendor?.dnsName}:VENDOR_DNS_NAME: address`,
      name: 'addingBy',
      label: $localize`:@@addSiteModalAddSiteByVendorDNSLabel:${environment?.vendor?.dnsName}:VENDOR_DNS_NAME: address`,
      testId: 'add-site-modal-keendns-radio-button',
    },
  ];

  setAddingBy(addingBy: any): void {
    this.addingBy = addingBy;

    if (addingBy === 'keendns') {
      this.form.controls.serviceTag.reset();
    } else if (addingBy === 'servicetag') {
      this.form.controls.keenDns.reset();
    }
  }

  validationConfig = {
    name: getValidationConfig([CustomValidators.maxlength(64)]),
    keenDnsControl: getValidationConfig([
      CustomValidators.pattern(
        KEEN_DNS_PATTERN,
        $localize`:@@addSiteModalVendorDNSFieldFormatError:Invalid ${environment?.vendor?.dnsName}:VENDOR_DNS_NAME: address format. Use "${environment?.vendor?.dnsExample}:VENDOR_DNS_EXAMPLE:"`,
      ),
    ]),
    serviceTagControl: getValidationConfig([
      CustomValidators.pattern(
        SERVICE_CODE_PATTERN,
        $localize`:@@addSiteModalServiceTagFieldFormatError:Invalid service tag format. Use "111-111-111-111-111"`,
      ),
    ]),
    login: getValidationConfig([CustomValidators.required()]),
    password: getValidationConfig([CustomValidators.required()]),
    ...(!environment.newRoleModel
      ? { isPrivate: getValidationConfig([CustomValidators.required()]) }
      : {}),
  };

  form = this.formBuilder.nonNullable.group(
    {
      name: this.formBuilder.nonNullable.control('', {
        validators: this.validationConfig.name.validators,
      }),
      serviceTag: this.formBuilder.nonNullable.control('', {
        updateOn: 'change',
        validators: this.validationConfig.serviceTagControl.validators,
      }),
      keenDns: this.formBuilder.nonNullable.control('', {
        updateOn: 'change',
        validators: this.validationConfig.keenDnsControl.validators,
      }),
      login: this.formBuilder.nonNullable.control('', {
        validators: this.validationConfig.login.validators,
      }),
      password: this.formBuilder.nonNullable.control('', {
        validators: this.validationConfig.password.validators,
      }),
      ...(!environment.newRoleModel
        ? {
            isPrivate: this.formBuilder.nonNullable.control(false, {
              validators: this.validationConfig.isPrivate?.validators,
            }),
          }
        : {}),
    },
    { validators: [this.getFormValidator()] },
  );

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  close(): void {
    this.dialogRef.close(false);
  }

  addSite(): void {
    if (!this.isSubmitting() && this.form.valid) {
      this.isSubmitting.set(true);

      this.data
        .addSite({
          name: this.form.value.name?.trim() || null,
          keenDns: this.form.value.keenDns,
          serviceTag: this.normalizeServiceTag(this.form.value.serviceTag),
          login: this.form.value.login,
          password: this.form.value.password,
          ...(!environment.newRoleModel
            ? { isPrivate: this.form.value.isPrivate }
            : {}),
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.isSubmitting.set(false)),
        )
        .subscribe((isClose) => {
          if (isClose) {
            this.dialogRef.close(true);
          }
        });
    }
  }

  private normalizeServiceTag(serviceTag?: string): string | undefined {
    if (!serviceTag) {
      return;
    }

    const regExp = /-/g;

    return serviceTag.replace(regExp, '');
  }

  private getFormValidator(): ValidatorFn {
    return (formGroup: AbstractControl) => {
      const serviceTagControl = formGroup.get('serviceTag');
      const keenDnsControl = formGroup.get('keenDns');

      if (this.addingBy === 'servicetag' && isEmpty(serviceTagControl?.value)) {
        serviceTagControl?.setErrors({ required: true });
      } else if (
        this.addingBy === 'keendns' &&
        isEmpty(keenDnsControl?.value)
      ) {
        keenDnsControl?.setErrors({ required: true });
      }

      return null;
    };
  }

  protected readonly environment = environment;
  protected readonly InfoTypeEnum = InfoTypeEnum;
}
